import { useAuthStore } from "~/store/auth/auth.store";
import { isAuthLoggingOut } from "~/utils/helpers/auth/lifecycle";
import { forbiddenError } from "~/utils/route-access";
import { RoleSyncResultEnum } from "~/utils/enums/auth.enums";

/**
 * Re-validates the user's roles when the tab regains visibility — e.g. after an
 * admin changed their rights while the tab was idle. Navigation already re-checks
 * via auth.global; this closes the idle-tab gap. RULE: if the roles changed, show
 * the "Vos droits ont été modifiés" error page (sticky — see auth.global + error.vue) so
 * the user acknowledges it — unless they only gained roles ranked below their highest
 * one (LOWER_ROLES_GAINED). Runs on the visibility event only (never
 * mid-navigation), so it can't race a route change.
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return;

  const REVALIDATE_THROTTLE_MS = 5000;
  let lastCheck = 0;

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastCheck < REVALIDATE_THROTTLE_MS) return;
    lastCheck = Date.now();

    nuxtApp.runWithContext(async () => {
      try {
        const authStore = useAuthStore();
        if (!authStore.isLoggedIn()) return;

        const result = await authStore.revalidateRoles();
        if (isAuthLoggingOut()) return;

        // The session died while the tab was idle. Leaving the protected page on screen
        // would show content to a user who no longer holds any role — take the same exit
        // auth.global takes for a logged-out session.
        if (result === RoleSyncResultEnum.SESSION_LOST) {
          await authStore.handleSessionLoss();
          return;
        }

        if (result === RoleSyncResultEnum.CHANGED) {
          authStore.roleConflictPending = true;
          showError(forbiddenError());
        }
      } catch (error) {
        // Nothing awaits this callback, so an escaping rejection would be unhandled. A
        // failed idle-tab re-check needs no UI: the next visibility change or navigation
        // retries it.
        console.error("roles-focus-revalidate: revalidateRoles failed", error);
      }
    });
  };

  document.addEventListener("visibilitychange", onVisible);
});
